import { Button } from '@/components/ui/button';
import ModalResponsive from '@/components/commons/modal-responsive';
import { useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import { resendVerificationEmail } from '@/lib/actions/customer/action';
import { Input } from '@/components/ui/input';
import * as yup from 'yup';
import { Formik, Form } from 'formik';
import { LoaderIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ModalResendVerificationProps extends React.HtmlHTMLAttributes<HTMLElement> {
  openModal?: boolean;
  setOpenModal?: (open: boolean) => void;
  host?: string;
}

const validationSchema = yup.object({
  email: yup.string().email('Please enter a valid email address').required('Email is required'),
});

export default function ModalResendVerification({
  openModal,
  setOpenModal,
  host,
}: ModalResendVerificationProps) {
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: { email: string }) => {
    const { email } = values;
    setLoading(true);

    const response = await resendVerificationEmail(host as string, { email });

    if (response.success) {
      toast({
        title: 'Success',
        description: 'Verification email has been resent!',
        variant: 'success',
      });
    } else {
      toast({
        title: 'Error',
        description: response.message || 'Failed to resend verification email.',
        variant: 'destructive',
      });
    }

    setLoading(false);
    setOpenModal?.(false);
  };

  return (
    <ModalResponsive
      openModal={openModal}
      setOpenModal={setOpenModal}
      title='Resend Verification Email'
      className='max-w-xl'
    >
      <div className='px-7'>
        <div className=''>
          <p className='text-sm'>
            Enter your email address to resend the verification email. We will resend the
            verification email to the address you provide.
          </p>
        </div>
        <div className='py-4'>
          <Formik
            initialValues={{ email: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange, handleBlur, errors, touched, isSubmitting }) => (
              <Form>
                <div className='mb-4'>
                  <Input
                    type='email'
                    name='email'
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder='Enter your email'
                    className='mb-4'
                    required
                  />
                  {touched.email && errors.email && (
                    <div className='text-left text-sm text-red-500'>{errors.email}</div>
                  )}
                </div>

                <div className='flex w-full justify-center gap-x-4'>
                  <Button
                    variant='outline'
                    className='h-full flex-1 py-3'
                    onClick={() => setOpenModal?.(false)}
                  >
                    <span className='mx-4'>Cancel</span>
                  </Button>
                  <Button
                    type='submit'
                    className={cn(`theme_button  w-full flex-1  text-sm`)}
                    disabled={isSubmitting || loading}
                  >
                    <span className='mx-4'>
                      {loading ? <LoaderIcon className='h-5 w-5' /> : 'Resend Verification'}
                    </span>
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </ModalResponsive>
  );
}
